import { useEffect, useState } from 'react';

/**
 * SSR-safe max-width media query hook.
 *
 * Use this instead of `react-responsive`'s `useMediaQuery`: Vite/Rollup can
 * mis-interop that CJS package so the named export lands on the default
 * MediaQuery component (`Ka.useMediaQuery is not a function` in prod bundles).
 */
export function useMaxWidth(maxWidthPx: number): boolean {
    const query = `(max-width: ${maxWidthPx}px)`;

    const [matches, setMatches] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }
        return window.matchMedia(query).matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mql = window.matchMedia(query);
        const onChange = () => setMatches(mql.matches);

        onChange();
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        }

        // Safari < 14
        mql.addListener(onChange);
        return () => mql.removeListener(onChange);
    }, [query]);

    return matches;
}

export default useMaxWidth;
